import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Upload, FileText, Calendar, Users, Clock, AlertTriangle, 
  CheckCircle2, Loader2, Plus, ArrowRight, ChevronRight,
  FileQuestion, Wand2, ListTodo, Building2, User, BarChart3,
  AlertCircle, Download, Eye, Trash2, RefreshCw, X
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const MEETING_TYPES = [
  'Main Contractor',
  'Subcontractor', 
  'Consultant Coordination',
  'Safety',
  'Progress',
  'Technical',
  'Other'
];

const STATUS_COLORS = {
  'Uploaded': 'bg-blue-100 text-blue-700 border-blue-200',
  'Parsing': 'bg-amber-100 text-amber-700 border-amber-200',
  'Parsed': 'bg-purple-100 text-purple-700 border-purple-200',
  'Confirmed': 'bg-green-100 text-green-700 border-green-200',
  'Superseded': 'bg-slate-100 text-slate-700 border-slate-200'
};

const MeetingMinutes = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [minutes, setMinutes] = useState([]);
  const [stats, setStats] = useState(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  
  // Upload form state - now supports multiple files
  const [uploadForm, setUploadForm] = useState({
    files: [],
    meeting_type: 'Progress',
    meeting_title: '',
    meeting_date: new Date().toISOString().split('T')[0],
    meeting_time: '',
    location: '',
    chairperson: '',
    minutes_taker: ''
  });
  
  // Filters
  const [filters, setFilters] = useState({
    meeting_type: '',
    status: ''
  });

  useEffect(() => {
    loadMinutes();
    loadStats();
  }, [projectId, filters]);

  const loadMinutes = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (projectId) params.append('project_id', projectId);
      if (filters.meeting_type) params.append('meeting_type', filters.meeting_type);
      if (filters.status) params.append('status', filters.status);
      
      const response = await api.get(`/meeting-minutes?${params}`);
      setMinutes(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load meeting minutes');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!projectId) return;
    try {
      const response = await api.get(`/meeting-minutes/stats/${projectId}`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const handleFileSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = [];
    for (const file of newFiles) {
      const ext = file.name.toLowerCase().split('.').pop();
      if (!['pdf', 'docx'].includes(ext)) {
        toast.error(`${file.name}: Only PDF and DOCX files are supported`);
        continue;
      }
      // Check for duplicates
      if (!uploadForm.files.some(f => f.name === file.name && f.size === file.size)) {
        validFiles.push(file);
      }
    }
    if (validFiles.length > 0) {
      setUploadForm({ ...uploadForm, files: [...uploadForm.files, ...validFiles] });
    }
  };

  const removeFile = (index) => {
    setUploadForm({
      ...uploadForm,
      files: uploadForm.files.filter((_, i) => i !== index)
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleUpload = async () => {
    if (uploadForm.files.length === 0 || !uploadForm.meeting_title || !uploadForm.meeting_date) {
      toast.error('Please select at least one file and fill in required fields');
      return;
    }

    setUploading(true);
    setUploadProgress(10);

    try {
      const formData = new FormData();
      // Append all files
      uploadForm.files.forEach((file, index) => {
        formData.append('files', file);
      });
      formData.append('project_id', projectId);
      formData.append('meeting_type', uploadForm.meeting_type);
      formData.append('meeting_title', uploadForm.meeting_title);
      formData.append('meeting_date', uploadForm.meeting_date);
      if (uploadForm.meeting_time) formData.append('meeting_time', uploadForm.meeting_time);
      if (uploadForm.location) formData.append('location', uploadForm.location);
      if (uploadForm.chairperson) formData.append('chairperson', uploadForm.chairperson);
      if (uploadForm.minutes_taker) formData.append('minutes_taker', uploadForm.minutes_taker);

      setUploadProgress(30);

      const response = await api.post('/meeting-minutes/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setUploadProgress(60);
      toast.success('Minutes uploaded successfully');
      
      // Auto-trigger extraction
      toast.info('Running AI extraction...');
      setUploadProgress(80);
      
      try {
        await api.post(`/meeting-minutes/${response.data.id}/extract`);
        toast.success('AI extraction completed!');
      } catch (extractError) {
        toast.warning('Upload successful, but extraction failed. You can retry later.');
      }

      setUploadProgress(100);
      setUploadDialogOpen(false);
      setUploadForm({
        files: [],
        meeting_type: 'Progress',
        meeting_title: '',
        meeting_date: new Date().toISOString().split('T')[0],
        meeting_time: '',
        location: '',
        chairperson: '',
        minutes_taker: ''
      });
      loadMinutes();
      loadStats();

    } catch (error) {
      toast.error('Failed to upload minutes: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (minutesId) => {
    if (!window.confirm('Are you sure you want to delete these minutes?')) return;
    
    try {
      await api.delete(`/meeting-minutes/${minutesId}`);
      toast.success('Minutes deleted');
      loadMinutes();
      loadStats();
    } catch (error) {
      toast.error('Failed to delete minutes');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          {/* Back Button - consistent with other modules */}
          <Button
            variant="ghost"
            onClick={() => navigate(`/projects/${projectId}`)}
            className="text-slate-600 hover:text-slate-900 mb-3 -ml-2 text-sm"
            data-testid="back-to-project-btn"
          >
            ← Back to Project
          </Button>
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="text-xl sm:text-2xl font-barlow font-bold uppercase tracking-wider text-slate-900">
                Meeting Minutes & Actions
              </h1>
              <p className="text-xs sm:text-sm text-slate-500 mt-1">
                Upload minutes, extract actions with AI, and track to completion
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/projects/${projectId}/action-packs`)}
                className="rounded-sm text-xs sm:text-sm"
                data-testid="action-packs-btn"
              >
                <Building2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Action Packs</span>
              </Button>
              <Button
                size="sm"
                onClick={() => setUploadDialogOpen(true)}
                className="rounded-sm bg-blue-600 hover:bg-blue-700 text-xs sm:text-sm"
                data-testid="upload-minutes-btn"
              >
                <Upload className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Upload Minutes</span>
                <span className="sm:hidden">Upload</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-4">
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Total</p>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_meetings}</p>
                  </div>
                  <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">This Month</p>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.meetings_this_month}</p>
                  </div>
                  <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Actions</p>
                    <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_actions}</p>
                  </div>
                  <ListTodo className="h-6 w-6 sm:h-8 sm:w-8 text-slate-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-slate-200">
              <CardContent className="p-3 sm:pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-slate-500">Open</p>
                    <p className="text-xl sm:text-2xl font-bold text-amber-600">{stats.open_actions}</p>
                  </div>
                  <Clock className="h-6 w-6 sm:h-8 sm:w-8 text-amber-500" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-red-200 bg-red-50/50 col-span-2 sm:col-span-1">
              <CardContent className="p-3 sm:pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-red-600">Overdue</p>
                    <p className="text-xl sm:text-2xl font-bold text-red-600">{stats.overdue_actions}</p>
                  </div>
                  <AlertTriangle className="h-6 w-6 sm:h-8 sm:w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-2 sm:gap-4 items-center">
          <Select
            value={filters.meeting_type}
            onValueChange={(value) => setFilters({ ...filters, meeting_type: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-[140px] sm:w-[180px] rounded-sm text-xs sm:text-sm">
              <SelectValue placeholder="Meeting Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {MEETING_TYPES.map(type => (
                <SelectItem key={type} value={type}>{type}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select
            value={filters.status}
            onValueChange={(value) => setFilters({ ...filters, status: value === 'all' ? '' : value })}
          >
            <SelectTrigger className="w-[120px] sm:w-[150px] rounded-sm text-xs sm:text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="Uploaded">Uploaded</SelectItem>
              <SelectItem value="Parsing">Parsing</SelectItem>
              <SelectItem value="Parsed">Parsed</SelectItem>
              <SelectItem value="Confirmed">Confirmed</SelectItem>
            </SelectContent>
          </Select>
          
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadMinutes}
            className="rounded-sm"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>

        {/* Minutes List */}
        <Card className="border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-xs sm:text-sm font-bold uppercase tracking-wider text-slate-600">
              Meeting Minutes Register
            </CardTitle>
          </CardHeader>
          <CardContent className="p-2 sm:p-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : minutes.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No meeting minutes found</p>
                <p className="text-sm text-slate-400 mt-1">Upload your first minutes to get started</p>
              </div>
            ) : (
              <>
                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-bold uppercase text-xs text-slate-500">Date</th>
                        <th className="text-left py-3 px-2 font-bold uppercase text-xs text-slate-500">Type</th>
                        <th className="text-left py-3 px-2 font-bold uppercase text-xs text-slate-500">Title</th>
                        <th className="text-left py-3 px-2 font-bold uppercase text-xs text-slate-500">Status</th>
                        <th className="text-center py-3 px-2 font-bold uppercase text-xs text-slate-500">Actions</th>
                        <th className="text-center py-3 px-2 font-bold uppercase text-xs text-slate-500">Open</th>
                        <th className="text-center py-3 px-2 font-bold uppercase text-xs text-slate-500">Overdue</th>
                        <th className="text-right py-3 px-2 font-bold uppercase text-xs text-slate-500"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {minutes.map((item) => (
                        <tr 
                          key={item.id} 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => navigate(`/meeting-minutes/${item.id}`)}
                          data-testid={`minutes-row-${item.id}`}
                        >
                          <td className="py-3 px-2">
                            <span className="font-mono text-sm">{formatDate(item.meeting_date)}</span>
                          </td>
                          <td className="py-3 px-2">
                            <Badge variant="outline" className="rounded-sm text-xs">
                              {item.meeting_type}
                            </Badge>
                          </td>
                          <td className="py-3 px-2">
                            <span className="font-medium text-slate-900">{item.meeting_title}</span>
                            {item.chairperson && (
                              <p className="text-xs text-slate-500">Chair: {item.chairperson}</p>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <Badge className={`rounded-sm text-xs ${STATUS_COLORS[item.status] || ''}`}>
                              {item.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className="font-semibold">{item.total_actions || 0}</span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`font-semibold ${item.open_actions > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
                              {item.open_actions || 0}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            <span className={`font-semibold ${item.overdue_actions > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                              {item.overdue_actions || 0}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDelete(item.id);
                                }}
                                className="h-8 w-8 p-0 text-slate-400 hover:text-red-600"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Mobile Card View */}
                <div className="md:hidden space-y-3">
                  {minutes.map((item) => (
                    <div
                      key={item.id}
                      className="border border-slate-200 rounded-lg p-3 hover:bg-slate-50 cursor-pointer"
                      onClick={() => navigate(`/meeting-minutes/${item.id}`)}
                      data-testid={`minutes-card-${item.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="font-medium text-slate-900 text-sm truncate">{item.meeting_title}</h3>
                          <p className="text-xs text-slate-500 mt-0.5">{formatDate(item.meeting_date)}</p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(item.id);
                            }}
                            className="h-7 w-7 p-0 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                          <ChevronRight className="h-4 w-4 text-slate-400" />
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className="rounded-sm text-[10px]">
                          {item.meeting_type}
                        </Badge>
                        <Badge className={`rounded-sm text-[10px] ${STATUS_COLORS[item.status] || ''}`}>
                          {item.status}
                        </Badge>
                        {item.total_actions > 0 && (
                          <span className="text-[10px] text-slate-500">
                            {item.total_actions} actions
                          </span>
                        )}
                        {item.open_actions > 0 && (
                          <span className="text-[10px] text-amber-600 font-medium">
                            {item.open_actions} open
                          </span>
                        )}
                        {item.overdue_actions > 0 && (
                          <span className="text-[10px] text-red-600 font-medium">
                            {item.overdue_actions} overdue
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl font-barlow font-bold uppercase">
                Upload Meeting Minutes
              </DialogTitle>
              <DialogDescription>
                Upload PDF or DOCX minutes. AI will extract actions, issues, and risks.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* File Upload with Drag & Drop */}
              <div
                className={`border-2 border-dashed rounded-sm p-6 text-center cursor-pointer transition-colors
                  ${isDragging ? 'border-blue-500 bg-blue-50' : 
                    uploadForm.files.length > 0 ? 'border-green-400 bg-green-50' : 'border-slate-300 hover:border-blue-400'}`}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept=".pdf,.docx"
                  multiple
                  onChange={handleFileSelect}
                />
                {uploadForm.files.length > 0 ? (
                  <div className="space-y-2">
                    {uploadForm.files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-3 py-2">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4 text-green-600" />
                          <span className="text-sm font-medium text-green-700">{file.name}</span>
                          <span className="text-xs text-green-500">({Math.round(file.size / 1024)} KB)</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 text-center">Click or drag to add more files</p>
                  </div>
                ) : (
                  <>
                    <Upload className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm text-slate-500">Drag & drop PDF or DOCX files here</p>
                    <p className="text-xs text-slate-400">or click to browse</p>
                  </>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Meeting Type *
                  </Label>
                  <Select
                    value={uploadForm.meeting_type}
                    onValueChange={(value) => setUploadForm({ ...uploadForm, meeting_type: value })}
                  >
                    <SelectTrigger className="mt-1 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MEETING_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Meeting Date *
                  </Label>
                  <Input
                    type="date"
                    value={uploadForm.meeting_date}
                    onChange={(e) => setUploadForm({ ...uploadForm, meeting_date: e.target.value })}
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                  Meeting Title *
                </Label>
                <Input
                  value={uploadForm.meeting_title}
                  onChange={(e) => setUploadForm({ ...uploadForm, meeting_title: e.target.value })}
                  placeholder="e.g., Site Progress Meeting #12"
                  className="mt-1 rounded-sm"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Chairperson
                  </Label>
                  <Input
                    value={uploadForm.chairperson}
                    onChange={(e) => setUploadForm({ ...uploadForm, chairperson: e.target.value })}
                    placeholder="Name"
                    className="mt-1 rounded-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                    Location
                  </Label>
                  <Input
                    value={uploadForm.location}
                    onChange={(e) => setUploadForm({ ...uploadForm, location: e.target.value })}
                    placeholder="Site office"
                    className="mt-1 rounded-sm"
                  />
                </div>
              </div>

              {uploading && (
                <div className="space-y-2">
                  <Progress value={uploadProgress} className="h-2" />
                  <p className="text-xs text-slate-500 text-center">
                    {uploadProgress < 60 ? 'Uploading...' : 'Running AI extraction...'}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUploadDialogOpen(false)}
                disabled={uploading}
                className="rounded-sm"
              >
                Cancel
              </Button>
              <Button
                onClick={handleUpload}
                disabled={uploading || uploadForm.files.length === 0 || !uploadForm.meeting_title}
                className="rounded-sm bg-blue-600 hover:bg-blue-700"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4 mr-2" />
                    Upload & Extract
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default MeetingMinutes;
